import { runInAction } from "mobx"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { LockRewardRepository, LockRewardsOnChainTransport } from "@/domains/lock-rewards"
import type { ILockApiTransport, ILockRepository, IWalletEventStrategy } from "@/domains/lock/interfaces"
import type { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import type { LockApiData } from "@/domains/lock/types"
import type { IAddressSource } from "@/domains/manager/interfaces"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IVoteOnChainTransport } from "@/domains/vote/interfaces"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { GaugeVotedEvent } from "@/domains/gauge/events"
import { OrchestratedQuery } from "@/domains/library"
import {
  LockAmountIncreasedEvent,
  LockCreatedEvent,
  LockDurationExtendedEvent,
  LockDurationMultiExtendedEvent,
  LockMergedEvent,
  LockReceivedEvent,
  LockResetEvent,
  LockTokensWithdrawnEvent,
  LockTransferredOutEvent,
  LockVotedEvent,
} from "@/domains/lock/events"
import { convertApiResponseToLockStoreDAO } from "@/domains/lock/lock-converter"
import { LockOrchestratorRefreshStrategy } from "@/domains/lock/lock-orchestrator-refresh-strategy"
import { LockStore } from "@/domains/lock/lock-store"
import { WalletEventStrategy } from "@/domains/lock/wallet-event-strategy"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

export class LockOrchestrator {
  private loadApiLocksQuery!: OrchestratedQuery<Array<LockApiData>>
  private loadChainLockInfoQuery: OrchestratedQuery<bigint[]> | undefined
  private loadChainVotingInfoQuery: OrchestratedQuery<any> | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private lockCreatedHandler: ((event: LockCreatedEvent) => void) | undefined
  private lockTransferredHandler: ((event: LockTransferredOutEvent) => void) | undefined
  private lockReceivedHandler: ((event: LockReceivedEvent) => void) | undefined
  private lockAmountIncreasedHandler: ((event: LockAmountIncreasedEvent) => Promise<void>) | undefined
  private lockDurationExtendedHandler: ((event: LockDurationExtendedEvent) => void) | undefined
  private lockDurationMultiExtendedHandler: ((event: LockDurationMultiExtendedEvent) => Promise<void>) | undefined
  private lockMergedHandler: ((event: LockMergedEvent) => void) | undefined
  private lockResetHandler: ((event: LockResetEvent) => void) | undefined
  private lockTokensWithdrawnHandler: ((event: LockTokensWithdrawnEvent) => void) | undefined
  private gaugeVotedHandler: ((event: GaugeVotedEvent) => Promise<void>) | undefined
  private lockVotedHandler: ((event: LockVotedEvent) => Promise<void>) | undefined

  constructor(
    private readonly lockRepository: ILockRepository,
    private readonly lockApiTransport: ILockApiTransport,
    private readonly lockOnChainTransport: LockOnChainTransport,
    private readonly walletStore: IWalletConnectionStore,
    private readonly lockRewardsOnChainTransport: LockRewardsOnChainTransport,
    private readonly lockRewardRepository: LockRewardRepository,
    private readonly tokenLookup: ITokenLookup,
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly eventBus: IEventBus,
    private readonly voteOnChainTransport: IVoteOnChainTransport,
    private readonly addressSource?: IAddressSource,
    private readonly queryNamePrefix?: string,
    private readonly walletEventStrategy: IWalletEventStrategy = new WalletEventStrategy(),
    private readonly enableRewards: boolean = true, // Default to true for normal locks
  ) {}

  initialize(
    loadLocksQuery: OrchestratedQuery<Array<LockApiData>>,
    loadLockInfoQuery?: OrchestratedQuery<bigint[]>,
    loadVotingInfoQuery?: OrchestratedQuery<any>,
  ) {
    this.loadApiLocksQuery = loadLocksQuery
    this.loadChainLockInfoQuery = loadLockInfoQuery
    this.loadChainVotingInfoQuery = loadVotingInfoQuery

    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to lock creation events
    this.lockCreatedHandler = this.handleLockCreated.bind(this)
    this.eventBus.subscribe(LockCreatedEvent.TYPE, this.lockCreatedHandler)

    // Subscribe to lock transfer events
    this.lockTransferredHandler = this.handleLockTransferred.bind(this)
    this.eventBus.subscribe(LockTransferredOutEvent.TYPE, this.lockTransferredHandler)

    // Subscribe to lock received events
    this.lockReceivedHandler = this.handleLockReceived.bind(this)
    this.eventBus.subscribe(LockReceivedEvent.TYPE, this.lockReceivedHandler)

    // Subscribe to lock amount increased events
    this.lockAmountIncreasedHandler = this.handleLockAmountIncreased.bind(this)
    this.eventBus.subscribe(LockAmountIncreasedEvent.TYPE, this.lockAmountIncreasedHandler)

    // Subscribe to lock duration extended events
    this.lockDurationExtendedHandler = this.handleLockDurationExtended.bind(this)
    this.eventBus.subscribe(LockDurationExtendedEvent.TYPE, this.lockDurationExtendedHandler)

    // Subscribe to lock duration multi extended events
    this.lockDurationMultiExtendedHandler = this.handleLockDurationMultiExtended.bind(this)
    this.eventBus.subscribe(LockDurationMultiExtendedEvent.TYPE, this.lockDurationMultiExtendedHandler)

    // Subscribe to lock merged events
    this.lockMergedHandler = this.handleLockMerged.bind(this)
    this.eventBus.subscribe(LockMergedEvent.TYPE, this.lockMergedHandler)

    // Subscribe to lock reset events
    this.lockResetHandler = this.handleLockReset.bind(this)
    this.eventBus.subscribe(LockResetEvent.TYPE, this.lockResetHandler)

    // Subscribe to lock tokens withdrawn events
    this.lockTokensWithdrawnHandler = this.handleLockTokensWithdrawn.bind(this)
    this.eventBus.subscribe(LockTokensWithdrawnEvent.TYPE, this.lockTokensWithdrawnHandler)

    // Subscribe to gauge voted events to refresh votes for the specific lock
    this.gaugeVotedHandler = this.handleGaugeVoted.bind(this)
    this.eventBus.subscribe(GaugeVotedEvent.TYPE, this.gaugeVotedHandler)

    // Subscribe to lock voted events to immediately refresh vote data
    this.lockVotedHandler = this.handleLockVoted.bind(this)
    this.eventBus.subscribe(LockVotedEvent.TYPE, this.lockVotedHandler)
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Use strategy to determine if we should stop queries
    if (this.walletEventStrategy.shouldStopQueriesOnWalletDisconnect()) {
      this.loadApiLocksQuery.stop()
    }

    // Clear all locks from repository
    this.lockRepository.clearAll()
  }

  private async handleWalletConnect(_event: WalletConnectedEvent): Promise<void> {
    // Clear existing data when wallet connects
    this.lockRepository.clearAll()

    // Use strategy to determine if we should start queries
    if (this.walletEventStrategy.shouldStartQueriesOnWalletConnect()) {
      this.loadApiLocksQuery.start()
    }
  }

  private handleLockCreated(_event: LockCreatedEvent): void {
    this.loadApiLocksQuery.forceExecute()
  }

  private handleLockTransferred(event: LockTransferredOutEvent): void {
    const lock = this.lockRepository.find(event.lockId)
    if (lock) {
      this.lockRepository.removeLock(event.lockId)
    }

    this.loadApiLocksQuery.forceExecute()
  }

  private handleLockReceived(_event: LockReceivedEvent): void {
    this.loadApiLocksQuery.forceExecute()
  }

  private async handleLockAmountIncreased(_event: LockAmountIncreasedEvent): Promise<void> {
    // Add a small delay to allow RPC node to index the transaction state
    // Transaction is confirmed, but RPC node may need a moment to reflect the changes
    await new Promise((resolve) => setTimeout(resolve, 2000))
    
    try {
      await this.loadApiLocksQuery.forceExecute()
    } catch (error) {
      console.error("[LockOrchestrator] Failed to force execute loadApiLocksQuery after lock amount increased:", error)
    }
  }

  private handleLockDurationExtended(_event: LockDurationExtendedEvent): void {
    this.loadApiLocksQuery.forceExecute()
  }

  private async handleLockDurationMultiExtended(_event: LockDurationMultiExtendedEvent): Promise<void> {
    // Add a small delay to allow RPC node to index the transaction state
    // Transaction is confirmed, but RPC node may need a moment to reflect the changes
    await new Promise((resolve) => setTimeout(resolve, 2000))
    
    try {
      await this.loadApiLocksQuery.forceExecute()
    } catch (error) {
      console.error("[LockOrchestrator] forceExecute failed:", error)
    }
  }

  private handleLockMerged(event: LockMergedEvent): void {
    // Remove the burned NFT (fromTokenId) from repository
    const fromLock = this.lockRepository.find(event.fromTokenId)
    if (fromLock) {
      this.lockRepository.removeLock(event.fromTokenId)
    }

    // Refresh query to update the merged NFT (toTokenId)
    this.loadApiLocksQuery.forceExecute()
  }

  private handleLockReset(_event: LockResetEvent): void {
    // Refresh query to update the reset NFT
    this.loadApiLocksQuery.forceExecute()
  }

  private handleLockTokensWithdrawn(event: LockTokensWithdrawnEvent): void {
    // Remove the NFT from repository (it's been burned after withdrawal)
    const withdrawnLock = this.lockRepository.find(event.tokenId)
    if (withdrawnLock) {
      this.lockRepository.removeLock(event.tokenId)
    }

    // Refresh query to ensure repository is in sync
    this.loadApiLocksQuery.forceExecute()
  }

  /**
   * Handle gauge voted event - refresh votes for the specific lock that voted
   */
  private async handleGaugeVoted(event: GaugeVotedEvent): Promise<void> {
    const lock = this.lockRepository.find(Number(event.tokenId))
    if (lock) {
      try {
        const votes = await this.voteOnChainTransport.loadLastEpochVotes([lock.id])
        lock.updateLastEpochVotes(votes)
      } catch (error) {
        console.error(`Failed to refresh votes for lock ${lock.id} after vote:`, error)
      }
    }
  }

  /**
   * Handle lock voted event - immediately refresh vote data for all voted locks
   * This provides immediate feedback without waiting for on-chain watcher
   * 
   * We need to:
   * 1. Optimistically update hasVotedThisEpoch immediately for instant UI feedback
   * 2. Force execute the main loadApiLocksQuery to refresh hasVotedThisEpoch from on-chain (confirms/updates state)
   * 3. Also update lastEpochVotes for immediate vote history display
   */
  private async handleLockVoted(event: LockVotedEvent): Promise<void> {
    // Optimistically update hasVotedThisEpoch immediately for instant UI feedback
    // This ensures the UI updates immediately, even if the RPC call takes time
    runInAction(() => {
      for (const lockId of event.lockIds) {
        const lock = this.lockRepository.find(lockId)
        if (lock) {
          lock.updateStaticData({ hasVotedThisEpoch: true })
        }
      }
    })

    // Force execute the main query to refresh all lock data from on-chain
    // This will confirm/update the state once blockchain has propagated
    try {
      await this.loadApiLocksQuery.forceExecute()
    } catch (error) {
      console.error(`[LockOrchestrator] Failed to force execute loadApiLocksQuery:`, error)
    }

    // Also update lastEpochVotes for immediate vote history display
    // Iterate through all lock IDs that voted
    for (const lockId of event.lockIds) {
      const lock = this.lockRepository.find(lockId)
      if (lock) {
        try {
          await this.loadLastEpochVotesForLock(lock)
        } catch (error) {
          console.error(`[LockOrchestrator] Failed to refresh votes for lock ${lockId} after vote:`, error)
        }
      }
    }
  }

  get query(): OrchestratedQuery<Array<LockApiData>> {
    return this.loadApiLocksQuery
  }

  get lockInfoQuery(): OrchestratedQuery<bigint[]> | undefined {
    return this.loadChainLockInfoQuery
  }

  get votingInfoQuery(): OrchestratedQuery<any> | undefined {
    return this.loadChainVotingInfoQuery
  }

  /**
   * Load locks from API (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onApiLoadLocksTrigger(_query: IOrchestratedQuerySender<Array<LockApiData>>): Promise<Array<LockApiData>> {
    const currentAddress = this.addressSource
      ? this.addressSource.getCurrentAddress()
      : (this.walletStore.connectedAddress ?? null)
    if (!currentAddress) {
      return []
    }
    return await this.lockApiTransport.loadUserNfts(currentAddress)
  }

  /**
   * Load lock info from on-chain (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onChainLoadLockInfoTrigger(_query: IOrchestratedQuerySender<bigint[]>): Promise<bigint[]> {
    const currentAddress = this.addressSource
      ? this.addressSource.getCurrentAddress()
      : (this.walletStore.connectedAddress ?? null)
    if (!currentAddress) {
      return []
    }
    return await this.lockOnChainTransport.loadLockInfo(currentAddress)
  }

  /**
   * Load voting info from on-chain (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onChainLoadVotingInfoTrigger(_query: IOrchestratedQuerySender<any>): Promise<any> {
    return await this.lockOnChainTransport.loadVotingInfo()
  }

  /**
   * Callback for when locks query succeeds
   */
  onApiLoadLocksQuerySuccess(_sender: IOrchestratedQuerySender<Array<LockApiData>>, data: Array<LockApiData>): void {
    try {
      runInAction(() => {
        // Process all locks from API response (add new ones, update existing ones)
        for (let i = 0; i < data.length; i++) {
          const element = data[i]
          if (element) {
            this.processLockFromAPI(element)
          }
        }

        // Delta diff: Remove locks from repository that are no longer in the API response
        const apiLockIds = new Set(data.map((lock) => lock.id))
        const existingLocks = this.lockRepository.getAll()
        for (const lock of existingLocks) {
          if (!apiLockIds.has(lock.id)) {
            this.lockRepository.removeLock(lock.id)
          }
        }
      })
    } catch (error) {
      console.error("Error processing lock data:", error)
    }
  }

  /**
   * Callback for when locks query fails
   */
  onApiLoadLocksQueryFailed(_sender: IOrchestratedQuerySender<Array<LockApiData>>, error: Error): void {
    console.error("Failed to load user locks", error)
  }

  /**
   * Callback for when lock info query succeeds
   */
  onChainLoadLockInfoQuerySuccess(_sender: IOrchestratedQuerySender<bigint[]>, data: bigint[]): void {
    try {
      runInAction(() => {
        // Process on-chain lock info data
        // console.log("Lock info from on-chain:", data)
        // TODO: Process the on-chain data and update lock stores
        // This is where you'll experiment with the return values
      })
    } catch (error) {
      console.error("Error processing lock info data:", error)
    }
  }

  /**
   * Callback for when lock info query fails
   */
  onChainLoadLockInfoQueryFailed(_sender: IOrchestratedQuerySender<bigint[]>, error: Error): void {
    console.error("Failed to load lock info from on-chain", error)
  }

  /**
   * Callback for when voting info query succeeds
   */
  onLoadVotingInfoQuerySuccess(_sender: IOrchestratedQuerySender<any>, data: any): void {
    try {
      runInAction(() => {
        // Process on-chain voting info data
        // console.log("Voting info from on-chain:", data)
        // TODO: Process the voting info data
      })
    } catch (error) {
      console.error("Error processing voting info data:", error)
    }
  }

  /**
   * Callback for when voting info query fails
   */
  onLoadVotingInfoQueryFailed(_sender: IOrchestratedQuerySender<any>, error: Error): void {
    console.error("Failed to load voting info from on-chain", error)
  }

  /**
   * Process lock data from API and add to repository
   */
  private processLockFromAPI(apiData: LockApiData): void {
    const found = this.lockRepository.find(apiData.id)

    if (found) {
      // Update existing lock - convert API data to DAO format first
      const lockStoreDAO = convertApiResponseToLockStoreDAO(apiData)
      found.updateStaticData(lockStoreDAO)
      return
    }

    // Convert API data to store DAO
    const lockStoreDAO = convertApiResponseToLockStoreDAO(apiData)

    // Create refresh strategy for this lock
    const refreshStrategy = new LockOrchestratorRefreshStrategy(this.loadApiLocksQuery)

    // Create new lock store with rewards dependencies
    const lock = new LockStore(
      lockStoreDAO,
      this.lockRewardsOnChainTransport,
      this.lockRewardRepository,
      this.tokenLookup,
      this.orchestratedQueryStore,
      refreshStrategy,
      this.queryNamePrefix,
      this.enableRewards, // Pass through the enableRewards flag
    )

    // Add to repository
    this.lockRepository.add(lock)

    // Load last epoch votes for this lock
    this.loadLastEpochVotesForLock(lock)
  }

  /**
   * Load last epoch votes for a specific lock
   */
  private async loadLastEpochVotesForLock(lock: LockStore): Promise<void> {
    try {
      const votes = await this.voteOnChainTransport.loadLastEpochVotes([lock.id])
      lock.updateLastEpochVotes(votes)
    } catch (error) {
      console.error(`Failed to load last epoch votes for lock ${lock.id}:`, error)
    }
  }

  /**
   * Load last epoch votes for all locks in the repository
   * Useful for ensuring vote data is available when needed (e.g., when drawer opens)
   * Vote data is automatically loaded when locks are added, but this method ensures
   * all existing locks have their vote data loaded.
   */
  async loadLastEpochVotesForAllLocks(): Promise<void> {
    const allLocks = this.lockRepository.getAll()
    if (allLocks.length === 0) {
      return
    }

    try {
      // Load votes for all locks in parallel
      const votePromises = allLocks.map((lock) => this.loadLastEpochVotesForLock(lock))
      await Promise.all(votePromises)
    } catch (error) {
      console.error("Failed to load last epoch votes for all locks:", error)
    }
  }
}
